class FilterDropdownElement extends HTMLElement {
  connectedCallback() {
    this._details = this.querySelector('details');
    this._summary = this.querySelector('summary');
    this._panel = this.querySelector('.filter-dropdown__panel');
    this._close = this.querySelector('[data-ref="close"]');

    if (!this._details) {
      return;
    }

    this._controller = new AbortController();
    const { signal } = this._controller;

    if (this._close) {
      this._close.addEventListener('click', () => this.close(), { signal });
    }

    // Reposition the panel whenever it opens or the viewport resizes so it never
    // overflows the right edge of the screen.
    this._details.addEventListener('toggle', () => this.reposition(), { signal });

    this._resizeObserver = new ResizeObserver(() => this.reposition());
    this._resizeObserver.observe(document.documentElement);

    this.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && this._details.open) {
          this.close();
        }
      },
      { signal }
    );

    document.addEventListener(
      'click',
      (e) => {
        if (this._details.open && !this.contains(e.target)) {
          this._details.open = false;
        }
      },
      { signal }
    );

    // Toggling an option updates the query string live. All options across the
    // filter dropdowns share one param, so we append/remove the toggled value to
    // the current query (preserving order, so results — and chips — reflect the
    // order filters were added) and ask the async-region to navigate. Rebuilding
    // from the pathname drops Craft's paging param, resetting to the first page.
    this.addEventListener(
      'change',
      (e) => {
        const input = e.target;
        if (!input.matches('input[type="checkbox"]')) {
          return;
        }

        const url = new URL(window.location.href);
        const key = input.name;
        const current = url.searchParams.getAll(key);
        const next = input.checked
          ? [...current, input.value]
          : current.filter((value) => value !== input.value);

        const search = new URLSearchParams();
        next.forEach((value) => search.append(key, value));
        const href = search.toString() ? `${url.pathname}?${search}` : url.pathname;

        this.dispatchEvent(new CustomEvent('async-region:navigate', { bubbles: true, detail: { href } }));
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this._controller?.abort();
    this._resizeObserver?.disconnect();
  }

  reposition() {
    if (!this._panel || !this._details.open) {
      return;
    }

    this._panel.style.setProperty('--filter-dropdown-shift', '0px');

    const gutter = parseFloat(getComputedStyle(this).getPropertyValue('--grid-gutter')) || 0;
    const rect = this._panel.getBoundingClientRect();
    const overflowRight = rect.right - (window.innerWidth - gutter);

    if (overflowRight <= 0) {
      return;
    }

    // Never shift so far that the left edge crosses the left gutter.
    const shift = Math.min(overflowRight, rect.left - gutter);
    if (shift > 0) {
      this._panel.style.setProperty('--filter-dropdown-shift', `${-shift}px`);
    }
  }

  close() {
    this._details.open = false;
    this._summary?.focus();
  }
}

customElements.define('chh-filter-dropdown', FilterDropdownElement);

export default FilterDropdownElement;
